import classNames from 'classnames'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import Selection from 'components/molecules/Selection'
import { formatPrice } from 'lib/utils'
import InfiniteScroll from 'react-infinite-scroll-component'
import { IoSearch } from 'react-icons/io5'
import { CREATE_BOOKING_BTN_NEXT, CREATE_BOOKING_SELECTION } from '../../bookingUiTokens'

const CarsStep = ({
  t,
  values,
  search,
  handleSearchChange,
  carData,
  isLoadingCars,
  loadMore,
  pagination,
  selectedCarId,
  loadingCarId,
  handleSelectCar,
  setFieldValue,
  searchOptionsPlaceholder,
  isRTL,
  isCompanyBookingFlow,
  showCompanyBranchFilter,
  companyBranchOptions,
  branchesLoading,
  onBranchChange,
}) => (
  <div className="space-y-6 pt-2">
    {isCompanyBookingFlow && showCompanyBranchFilter && (
      <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm ring-1 ring-slate-900/[0.03] sm:p-5">
        <label htmlFor="create-booking-branch" className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-slate-600">
          {t('bookingProfile.labels.branch')}
        </label>
        <Selection
          onChange={item => {
            if (!item || item.id === undefined || item.id === null) return
            onBranchChange(item.id, setFieldValue)
          }}
          title={t('bookingProfile.placeholders.selectBranch')}
          list={companyBranchOptions}
          value={values.selectedBranchId}
          className={CREATE_BOOKING_SELECTION}
        />
      </div>
    )}

    {values.deliveryMethod === 'deliver' && (
      <p className="rounded-xl border border-sky-200/80 bg-sky-50/90 px-4 py-3 text-sm text-slate-700 ring-1 ring-sky-100/80">{t('bookingProfile.messages.deliveryCarsBanner')}</p>
    )}

    <div className="relative">
      <div className={classNames('pointer-events-none absolute inset-y-0 flex items-center', isRTL ? 'right-0 pr-3' : 'left-0 pl-3')}>
        <IoSearch className="h-4 w-4 text-slate-400" aria-hidden="true" />
      </div>
      <input
        onChange={handleSearchChange}
        value={search}
        type="text"
        name="search"
        id="search"
        className={classNames(
          'block w-full rounded-xl border border-slate-200/80 bg-white py-2.5 text-sm text-slate-800 shadow-sm focus:border-dark-500 focus:ring-2 focus:ring-dark-500/20',
          isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'
        )}
        placeholder={searchOptionsPlaceholder}
      />
    </div>

    <div className="w-full" id="scrollableCarDiv" style={{ overflow: 'auto', height: 'calc(100vh - 420px)', minHeight: '450px' }}>
      {isCompanyBookingFlow && showCompanyBranchFilter && !branchesLoading && companyBranchOptions.length === 0 ? (
        <EmptyState v2 description={t('bookingProfile.messages.noBranchesForCompany')} />
      ) : isLoadingCars && carData.length === 0 ? (
        <div className="flex h-52 w-full items-center justify-center">
          <LoadingIcon />
        </div>
      ) : carData.length === 0 ? (
        <EmptyState v2 description={t('bookingProfile.messages.noCarsAvailable')} />
      ) : (
        <InfiniteScroll
          dataLength={carData.length}
          next={loadMore}
          hasMore={pagination.currentPage < pagination.totalPage}
          loader={
            <div className="flex h-16 w-full items-center justify-center">
              <LoadingIcon />
            </div>
          }
          scrollThreshold={0.8}
          scrollableTarget="scrollableCarDiv"
          className="w-full"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
            {carData.map((rentalCarData, index) => {
              const isSelected = selectedCarId === rentalCarData._id
              return (
                <div
                  key={`${rentalCarData._id}-${index}`}
                  className={classNames(
                    'relative overflow-hidden rounded-2xl border-2 bg-white p-4 shadow-sm transition-all duration-200',
                    isSelected ? 'border-dark-600 ring-2 ring-dark-600/20' : 'border-slate-200/80 ring-1 ring-slate-900/[0.04] hover:shadow-md'
                  )}
                >
                  <div className="relative">
                    <img src={rentalCarData.carImages?.[0]?.url || '/placeholder-car.jpg'} alt="" className="mb-3 h-40 w-full rounded-xl object-cover" />
                    {rentalCarData.company?.logoUrl && (
                      <img src={rentalCarData.company.logoUrl} alt="" className="absolute right-2 top-2 h-14 w-14 rounded-lg border border-white object-cover shadow-md" />
                    )}
                  </div>
                  <h3 className="text-base font-extrabold tracking-tight text-slate-900">
                    {rentalCarData.car?.make || ''} {rentalCarData.car?.model || ''} ({rentalCarData.modelYear})
                  </h3>
                  <div className="mt-2 rounded-xl bg-slate-50/90 p-2.5 ring-1 ring-slate-200/60">
                    <p className="text-xs text-slate-600">
                      {rentalCarData.car?.marketSegment || ''} | {rentalCarData.car?.numberOfSeats || ''} | {rentalCarData.car?.shifter || ''} |{' '}
                      {rentalCarData.unlimitedKm || !rentalCarData.allowedKMPerDay ? 'Unlimited' : `${rentalCarData.allowedKMPerDay} km/day`}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">Distance: {rentalCarData.distance || '0'}</p>
                    {rentalCarData.isDeliveryAvailable && <p className="mt-1 text-xs font-semibold text-emerald-600">{t('bookingProfile.messages.availableForDelivery')}</p>}
                  </div>
                  <p className="mt-3 text-lg font-bold tabular-nums text-slate-900">
                    {formatPrice(rentalCarData.dailyRate || 0, true)} {t('common.currency')}/{t('bookingProfile.labels.perDay')}
                  </p>
                  <button
                    type="button"
                    onClick={() => handleSelectCar(rentalCarData._id, setFieldValue)}
                    disabled={loadingCarId === rentalCarData._id}
                    className={classNames(CREATE_BOOKING_BTN_NEXT, 'mt-3 w-full')}
                  >
                    {loadingCarId === rentalCarData._id
                      ? t('bookingProfile.buttons.processing')
                      : isSelected
                      ? t('bookingProfile.buttons.selected')
                      : t('bookingProfile.buttons.selectCar')}
                  </button>
                </div>
              )
            })}
          </div>
        </InfiniteScroll>
      )}
    </div>
  </div>
)

export default CarsStep
